import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, homeFor } from "../context/AuthContext";
import { fmtErr } from "../lib/api";
import { PROMOTER_TITLES } from "../lib/promoterTitles";
import { formatPlanPrice, usePricing } from "../hooks/usePricing";

const ROLES = [
  { id: "guest", label: "Guest", desc: "Free forever" },
  { id: "promoter", label: "Promoter", desc: "Promoter tools" },
  { id: "venue", label: "Venue", desc: "Venue tools" },
];

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [role, setRole] = useState(params.get("role") || "guest");
  const [form, setForm] = useState({ email: "", password: "", name: "", referral_code: params.get("ref") || localStorage.getItem("np_ref") || "", referral_source: !params.get("ref") && localStorage.getItem("np_ref") ? "handle" : undefined, promoter_title: "Party Promoter" });
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const { login, register } = useAuth();
  const pricing = usePricing();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = mode === "login"
        ? await login(form.email, form.password)
        : await register({ ...form, role, agreed });
      toast.success(mode === "login" ? "Welcome back to the night" : "Your NITEPASS is ready");
      navigate(homeFor(user), { replace: true });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#050505] px-4 py-12"
      style={{ paddingTop: "calc(3rem + env(safe-area-inset-top))" }}>
      <Link to="/" data-testid="auth-back-btn"
        className="glass absolute left-4 rounded-full p-2.5 text-zinc-300 transition-colors hover:text-neon-gold"
        style={{ top: "calc(1rem + env(safe-area-inset-top))" }}>
        <ArrowLeft size={18} />
      </Link>
      <div className="absolute left-1/4 top-0 h-80 w-80 rounded-full bg-neon-gold/15 blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 h-80 w-80 rounded-full bg-neon-violet/10 blur-[120px]" />
      <div className="relative w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/" data-testid="auth-logo">
            <img src="/logo-dark.png" alt="NITEPASS" className="mx-auto h-36 w-auto rounded-3xl transition-transform duration-200 hover:scale-[1.02]" />
          </Link>
          <p className="mt-3 text-sm text-zinc-500">Your pass to the night.</p>
        </div>
        <div className="glass rounded-3xl p-8">
          <div className="mb-6 grid grid-cols-2 rounded-full border border-white/10 bg-black/40 p-1">
            {["login", "register"].map((m) => (
              <button key={m} data-testid={`auth-mode-${m}`} type="button"
                onClick={() => setMode(m)}
                className={`rounded-full py-2 font-display text-sm font-bold transition-[background-color,color] duration-200 ${mode === m ? "bg-neon-gold text-black shadow-glow-gold" : "text-zinc-400 hover:text-white"}`}>
                {m === "login" ? "Log in" : "Sign up"}
              </button>
            ))}
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <input data-testid="auth-name-input" required placeholder="Your name"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-sm outline-none transition-[border-color] focus:border-neon-gold" />
                <div className="grid grid-cols-3 gap-2" data-testid="auth-role-select">
                  {ROLES.map((r) => (
                    <button key={r.id} type="button" data-testid={`auth-role-${r.id}`}
                      onClick={() => setRole(r.id)}
                      className={`rounded-xl border px-2 py-3 text-center transition-[border-color,background-color] duration-200 ${role === r.id ? "border-neon-gold bg-neon-gold/10" : "border-white/10 bg-black/40 hover:border-white/25"}`}>
                      <div className="font-display text-xs font-bold">{r.label}</div>
                      <div className="mt-0.5 text-[10px] text-zinc-500">
                        {r.id === "promoter" ? `${formatPlanPrice(pricing.promoter_monthly)}/mo or ${formatPlanPrice(pricing.promoter_yearly)}/yr` : r.id === "venue" ? `${formatPlanPrice(pricing.venue_monthly)}/mo or ${formatPlanPrice(pricing.venue_yearly)}/yr` : r.desc}
                      </div>
                    </button>
                  ))}
                </div>
                {role === "promoter" && (
                  <select data-testid="auth-promoter-title-select" value={form.promoter_title}
                    onChange={(e) => setForm({ ...form, promoter_title: e.target.value })}
                    className="w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-sm outline-none transition-[border-color] focus:border-neon-gold">
                    {PROMOTER_TITLES.map((title) => <option key={title} value={title}>{title}</option>)}
                  </select>
                )}
                <input data-testid="auth-referral-code-input" placeholder="Referral code (optional)"
                  value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                  className="w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-sm uppercase outline-none transition-[border-color] focus:border-neon-violet" />
              </>
            )}
            <input data-testid="auth-email-input" required type="email" placeholder="Email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-sm outline-none transition-[border-color] focus:border-neon-gold" />
            <input data-testid="auth-password-input" required type="password" placeholder="Password (min 6 chars)" minLength={6}
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full rounded-xl border border-white/10 bg-black/50 px-4 py-3 text-sm outline-none transition-[border-color] focus:border-neon-gold" />
            {mode === "register" && (
              <label data-testid="auth-agree-label"
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3.5 transition-colors duration-200 ${agreed ? "border-neon-gold/50 bg-neon-gold/5" : "border-white/10 bg-black/40"}`}>
                <input data-testid="auth-agree-checkbox" type="checkbox" checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#ffd700]" />
                <span className="text-[11px] leading-relaxed text-zinc-400">
                  I confirm I am <span className="font-bold text-white">18 years or older</span> and I agree to the{" "}
                  <Link to="/terms" className="font-bold text-neon-gold">Terms of Service</Link>,{" "}
                  <Link to="/privacy" className="font-bold text-neon-gold">Privacy Policy</Link> and{" "}
                  <Link to="/data-policy" className="font-bold text-neon-gold">Data Policy</Link>.
                </span>
              </label>
            )}
            <button data-testid="auth-submit-btn" disabled={busy || (mode === "register" && !agreed)} className="btn-neon w-full disabled:opacity-50">
              {busy ? "One sec..." : mode === "login" ? "Enter the Night" : "Create My Pass"}
            </button>
          </form>
          {mode === "register" && role !== "guest" && (
            <p className="mt-4 text-center text-xs text-zinc-500">
              {role === "promoter" ? "Promoter" : "Venue"} subscription activates after checkout — you can explore first.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
